/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import rrulePlugin from '@fullcalendar/rrule'
import timeGridPlugin from '@fullcalendar/timegrid'
import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Modal, ModalBody } from 'reactstrap'

import { PAGE_TITLE, ROUTE_TYPE } from '../../Configuration'
import { PascodeMetadata } from '../../assets/ts/data/pascode-metadata'
import {
  generateCodeOptionOnLength,
  getFilteredItems,
  getMAJCOMCode,
  getRecurringEventData,
  getUniqueCodes,
  scrollToTop,
} from '../../assets/ts/general/general'
import Buttons from '../../components/buttons/buttons'
import PageOverlayBusy from '../../components/page-overlay/page-overlay-busy'
import useCalendar from '../../hooks/use-calendar'
import usePageTitle from '../../hooks/use-page-title'
import usePascodeMetadata from '../../hooks/use-pascode-metadata'
import PageHeader from '../../layouts/header/page-header'
import { MetadataContext } from '../../store/metadata-context-provider'
import { PreviousPageContext } from '../../store/previous-page-provider'
import { RoleContext } from '../../store/role-context-provider'
import { UserContext } from '../../store/user-context-provider'
import {
  ALL_SELECT_OPTION,
  ALL_SELECT_OPTION_STRING,
  ALL_SELECT_VALUE,
  ALL_SELECT_VALUE_STRING,
  NONE_SELECT_OPTION_STRING,
} from '../../utils/constants/global-constants'
import CustomSelect from '../../utils/controls/custom-select'
import {
  Dropdown,
  optionsTypes,
} from '../../utils/controls/select-options-data'
import './calendar.css'

interface CalendarEvent {
  allDay?: boolean
  description?: string
  end?: Date | string // Optional end
  extendedProps?: Record<string, any>
  id: number | string
  start?: Date | string // Optional start
  title: string
}
interface CalendarInnerviewProps {
  onClose: () => void
}
interface EventDetailsProps {
  event: CalendarEvent | null
  onClose: () => void
}
interface IntialInputValues {
  categoryValue: number
  componentValue: number
  majcomValue: number
  sectionValue: number
}

const EventDetails: React.FC<EventDetailsProps> = ({ event, onClose }) => {
  if (!event) return null

  return (
    <>
      <div className='eventPopupHeader d-flex align-items-center p-3'>
        <h4
          aria-label='Event Details'
          className='color-primary poppins-semibold font-13'
          tabIndex={0}
        >
          {event.title}
        </h4>

        <button
          aria-label='Close'
          className='closeEventPopup no-border whitebg font-10 cursor color-primary'
          onClick={onClose}
          title='Close'
        >
          <span className='icon-close'></span>
        </button>
      </div>
      <div className='popup-body'>
        <div className='formInputRow'>
          <div className='col-lg-3 col-sm-12'>
            <p className='font-12 sourcesanspro'>
              <strong>Start Time:</strong> {event.start}
            </p>
          </div>
        </div>
        {event.end && (
          <div className='formInputRow'>
            <div className='col-lg-3 col-sm-12'>
              <p className='font-12 sourcesanspro'>
                <strong>End Time:</strong> {event.end}
              </p>
            </div>
          </div>
        )}
        <div className='formInputRow'>
          <div className='col-lg-3 col-sm-12'>
            <p className='font-12 sourcesanspro'>
              <strong>Description:</strong> {event.description}
            </p>
          </div>
        </div>
        <div className='formInputRow'>
          <div className='col-lg-3 col-sm-12'>
            <p className='font-12 sourcesanspro'>
              <strong>MAJCOM:</strong> {event.extendedProps?.MAJCOM}
            </p>
          </div>
        </div>
        <div className='formInputRow'>
          <div className='col-lg-3 col-sm-12'>
            <p className='font-12 sourcesanspro'>
              <strong>Section:</strong> {event.extendedProps?.Section}
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
const CalendarInnerview: React.FC<CalendarInnerviewProps> = (props) => {
  usePageTitle(PAGE_TITLE.CALENDAR)
  const navigate = useNavigate()
  const location = useLocation()
  const { setCurrentPage } = useContext(PreviousPageContext)
  const { userProfile } = useContext(UserContext)
  const {
    componentOptions,
    majcomOptions,
    majcoms,
    sectionOptions,
  } = useContext(MetadataContext)
  const {
    initialInquiryAndDueoutFilter,
    isAssignedComponentOwner,
    isCPOwner,
    isCSS,
    isCustomer,
    isLeadership,
    isMAJCOMOwner,
    isMPFOwner,
    isSectionChief,
    isSectionOwner,
    isSiteAdmin,
    isSupervisor,
    isTechnician,
  } = useContext(RoleContext)
  const userLoginRole = {
    CSSPASCODEFilter: initialInquiryAndDueoutFilter?.cssPASCODES,
    usersAssignedComponentCode: initialInquiryAndDueoutFilter?.assignedComponentCodeFilter,
    usersMajcomCodeFilter: initialInquiryAndDueoutFilter?.majcomCodeFilter,
    usersSectionCodeFilter: initialInquiryAndDueoutFilter?.sectionCodeFilter,
    usersServicingOrganizationFilter: initialInquiryAndDueoutFilter?.servicingOrganizationFilter,
    usersServicingOrganizationWithoutSectionFilter:
      initialInquiryAndDueoutFilter?.servicingOrganizationWithoutSectionFilter,
  }
  const { pascodeMetadata } = usePascodeMetadata({
    IsArchived: false,
  })

  const metadataContext = useContext(MetadataContext)
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([])
  const [categoriesOptions, setCategoriesOptions] = useState<Array<string>>([])
  const [showEventDetails, setShowEventDetails] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [intialInputValues, setIntialInputValues] = useState<IntialInputValues>({
    categoryValue: ALL_SELECT_VALUE,
    componentValue: ALL_SELECT_VALUE,
    majcomValue: ALL_SELECT_VALUE,
    sectionValue: ALL_SELECT_VALUE,
  })
  interface InputValues {
    AssignedComponentCode: number
    Category: number | string
    SectionCode: number
    ServicingMAJCOMCode: number
  }
  const [inputValues, setInputValues] = useState<InputValues>({
    AssignedComponentCode: ALL_SELECT_VALUE,
    Category: ALL_SELECT_VALUE_STRING,
    SectionCode: ALL_SELECT_VALUE,
    ServicingMAJCOMCode: ALL_SELECT_VALUE,
  })

  const { CalendarData, CalendarDataIsLoading, fetchCalendarData } = useCalendar({
    assignedComponentFilter: inputValues?.AssignedComponentCode,
    categoryFilter: ALL_SELECT_VALUE_STRING,
    isArchived: false,
    sectionFilter: inputValues?.SectionCode,
    servicingMajcomFilter: inputValues?.ServicingMAJCOMCode,
  })

  const [intialComponentDropdown, setIntialComponentDropdown] = useState<optionsTypes[]>([
    ALL_SELECT_OPTION,
  ])
  const [intialMajcomDropdown, setIntialMajcomDropdown] = useState<optionsTypes[]>([
    ALL_SELECT_OPTION,
  ])
  const [intialSectionDropdown, setIntialSectionDropdown] = useState<optionsTypes[]>([
    ALL_SELECT_OPTION,
  ])
  const [majcomDropDown, setMajcomDropdown] = useState<optionsTypes[]>([])

  const setintialOptions = (
    componentOptions: optionsTypes[],
    majcomOptions: optionsTypes[],
    sectionOptions: optionsTypes[],
    ownerIntialMajcom?: optionsTypes[]
  ) => {
    const componentValue = userProfile[0].AssignedComponentCode
    const servicingMAJCOMCode = getMAJCOMCode(userProfile[0].ServicingMAJCOM, majcoms)
    setIntialComponentDropdown(componentOptions)
    setIntialMajcomDropdown(majcomOptions)
    setMajcomDropdown(ownerIntialMajcom ? ownerIntialMajcom : majcomOptions)
    setIntialSectionDropdown(sectionOptions)
    setCategoryOptions(metadataContext.categoryOptions)
    const dropdownValues = {
      AssignedComponentCode: componentValue,
      SectionCode:
        sectionOptions.length === 1 ? (sectionOptions[0].value as number) : ALL_SELECT_VALUE,
      ServicingMAJCOMCode: servicingMAJCOMCode,
    }
    if (metadataContext.componentValue) {
      const option: Dropdown[] = majcoms
        .filter(
          ({ ParentComponentCode }) => ParentComponentCode === metadataContext.componentValue.value
        )
        .map(({ Label, Value }) => ({ label: Label, value: Value }))
      const intialmajcomCodes = majcomOptions.map(({ value }) => value)
      const options = option.filter(({ value }) => intialmajcomCodes.includes(value as number))
      if (options.length > 1) options.unshift(ALL_SELECT_OPTION)
      setMajcomDropdown(options as optionsTypes[])

      setInputValues((prev) => ({
        ...prev,
        AssignedComponentCode: metadataContext.componentValue.value,
        Category: metadataContext.categoryValue.value,
        SectionCode: metadataContext.sectionValue.value,
        ServicingMAJCOMCode: metadataContext.majcomValue.value,
      }))
      setIntialInputValues({
        categoryValue: ALL_SELECT_VALUE, // Add the missing property
        componentValue: dropdownValues.AssignedComponentCode as number,
        majcomValue: dropdownValues.ServicingMAJCOMCode as number,
        sectionValue: dropdownValues.SectionCode as number,
      })
    } else if (metadataContext.sectionValue || metadataContext.categoryValue) {
      setInputValues((prev) => ({
        ...prev,
        AssignedComponentCode:
          metadataContext.componentValue === null
            ? componentOptions.length === 1
              ? componentOptions[0].value
              : -1
            : metadataContext.componentValue.value,
        Category: metadataContext.categoryValue.value,
        SectionCode: metadataContext.sectionValue.value,
        ServicingMAJCOMCode:
          metadataContext.majcomValue === null
            ? majcomOptions.length === 1
              ? majcomOptions[0].value
              : -1
            : metadataContext.majcomValue.value,
      }))
      setIntialInputValues({
        categoryValue: ALL_SELECT_VALUE, // Add the missing property
        componentValue: dropdownValues.AssignedComponentCode as number,
        majcomValue: dropdownValues.ServicingMAJCOMCode as number,
        sectionValue: dropdownValues.SectionCode as number,
      })
    } else {
      if (!location.state?.filterValues) {
        setInputValues((prev) => ({
          ...prev,
          ...dropdownValues,
        }))
        setIntialInputValues({
          categoryValue: ALL_SELECT_VALUE, // Add the missing property
          componentValue: dropdownValues.AssignedComponentCode as number,
          majcomValue: dropdownValues.ServicingMAJCOMCode as number,
          sectionValue: dropdownValues.SectionCode as number,
        })
      }
    }
  }

  useEffect(() => {
    const assignedComponentOptions = componentOptions.filter(
      ({ value }) => value !== ALL_SELECT_VALUE
    )
    if (isCPOwner || isSiteAdmin) {
      const profileComponentCode = userProfile[0].AssignedComponentCode
      const majcomCodes = majcoms
        .filter((item) => item.ParentComponentCode === profileComponentCode)
        .map((item) => item.MAJCOMCode)
      const majcomOption = generateCodeOptionOnLength(majcomOptions, majcomCodes)
      setintialOptions(assignedComponentOptions, majcomOptions, sectionOptions, majcomOption)
    } else if (isAssignedComponentOwner) {
      const componentCodes = userLoginRole?.usersAssignedComponentCode
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomCodes = majcoms
        .filter((item) => componentCodes?.includes(item.ParentComponentCode))
        .map((item) => item.MAJCOMCode)
      const majcomOption = generateCodeOptionOnLength(majcomOptions, majcomCodes)
      setintialOptions(assignedComponentCodeOptions, majcomOption, sectionOptions)
    } else if (isMAJCOMOwner) {
      const majcomCode = userLoginRole?.usersMajcomCodeFilter
      const profileMajcomCode = getMAJCOMCode(userProfile[0].ServicingMAJCOM, majcoms)
      const majcomCodes = majcomCode.includes(profileMajcomCode)
        ? majcomCode
        : (majcomCode.unshift(profileMajcomCode), majcomCode)
      const componentCodes = majcoms
        .filter((item) => majcomCodes?.includes(item.MAJCOMCode))
        .map((item) => item.ParentComponentCode)
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOption = generateCodeOptionOnLength(majcomOptions, majcomCodes)
      setintialOptions(assignedComponentCodeOptions, majcomOption, sectionOptions)
    } else if (isLeadership || isMPFOwner) {
      const roleOrganizations =
        userLoginRole?.usersServicingOrganizationWithoutSectionFilter as string[]
      const profileOrganization = userProfile[0].ServicingOrganization
      const organizations = roleOrganizations.includes(profileOrganization)
        ? roleOrganizations
        : (roleOrganizations.unshift(profileOrganization), roleOrganizations)
      const filteredItems: PascodeMetadata[] = getFilteredItems(pascodeMetadata, organizations)
      const { componentCodes, servicingMAJCOMS } = getUniqueCodes(filteredItems)
      const majcomCodes = servicingMAJCOMS.map((item) => getMAJCOMCode(item, majcoms))

      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOption = generateCodeOptionOnLength(majcomOptions, majcomCodes)
      setintialOptions(assignedComponentCodeOptions, majcomOption, sectionOptions)
    } else if (isCSS) {
      const userCSSPASCODES = userLoginRole?.CSSPASCODEFilter
      const userProfilePASCODE = userProfile[0].PASCODE
      const cssPASCODES = userCSSPASCODES.includes(userProfilePASCODE)
        ? userCSSPASCODES
        : (userCSSPASCODES.unshift(userProfilePASCODE), userCSSPASCODES)
      const filteredItems = pascodeMetadata.filter((item) => cssPASCODES?.includes(item.PASCODE))
      const { componentCodes, servicingMAJCOMS } = getUniqueCodes(filteredItems)
      const majcomCodes = servicingMAJCOMS.map((item) => getMAJCOMCode(item, majcoms))
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOption = generateCodeOptionOnLength(majcomOptions, majcomCodes)
      setintialOptions(assignedComponentCodeOptions, majcomOption, sectionOptions)
    } else if (isSupervisor || isTechnician || isSectionChief || isSectionOwner) {
      const roleOrganizations = userLoginRole?.usersServicingOrganizationFilter as string[]
      const profileOrganization = userProfile[0].ServicingOrganization
      const organizations = roleOrganizations.includes(profileOrganization)
        ? roleOrganizations
        : (roleOrganizations.unshift(profileOrganization), roleOrganizations)
      const filteredItems = getFilteredItems(pascodeMetadata, organizations)
      const { componentCodes, servicingMAJCOMS } = getUniqueCodes(filteredItems)
      const majcomCodes = servicingMAJCOMS.map((item) => getMAJCOMCode(item, majcoms))
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCodes
      )
      const majcomOption = generateCodeOptionOnLength(majcomOptions, majcomCodes)
      const sectionCodes = userLoginRole?.usersSectionCodeFilter as number[]
      const sectionOption = generateCodeOptionOnLength(sectionOptions, sectionCodes)
      setintialOptions(assignedComponentCodeOptions, majcomOption, sectionOption)
    } else {
      const componentCode = [userProfile[0].AssignedComponentCode]
      const servicingMAJCOMCode = [getMAJCOMCode(userProfile[0].ServicingMAJCOM, majcoms)]
      const assignedComponentCodeOptions = generateCodeOptionOnLength(
        assignedComponentOptions,
        componentCode
      )
      const majcomOption = generateCodeOptionOnLength(majcomOptions, servicingMAJCOMCode)
      setintialOptions(assignedComponentCodeOptions, majcomOption, sectionOptions)
    }
  }, [
    isSiteAdmin,
    isCSS,
    isCPOwner,
    isAssignedComponentOwner,
    isMAJCOMOwner,
    pascodeMetadata,
    isCustomer,
    metadataContext,
  ])

  const fetchEvents = async () => {
    try {
      // Format the events from CalendarData
      const formattedEvents: CalendarEvent[] = await CalendarData.map((event) => {
        const RRules = event?.RRule ? JSON.parse(event?.RRule) : undefined
        const recurringEvents: any[] = getRecurringEventData(RRules, event, metadataContext)

        if (recurringEvents && recurringEvents.length > 0) {
          return recurringEvents
        } else {
          return {
            allDay: event.IsAllDayEvent,
            daysOfWeek: RRules?.daysOfWeek ? RRules.daysOfWeek : undefined,
            description: event.Description,
            end: event.EndDate,
            endRecur: RRules?.until ? RRules?.until : undefined,
            extendedProps: {
              Category: event.Category,
              IsArchived: event.IsArchived,
              MAJCOM:
                metadataContext?.majcomOptions.find((option) => option.value === event?.MAJCOMCode)
                  ?.label || 'Unknown',
              Section:
                metadataContext?.sectionOptions.find(
                  (option) => option.value === event?.SectionCode
                )?.label || 'Unknown',
            },
            id: event.eKey,
            rRule: RRules,
            start: event.StartDate,
            startRecur: RRules?.dtstart ? RRules?.dtstart : undefined,
            title: event.Title,
          }
        }
      })
      // Batch state updates to minimize re-renders
      setCalendarEvents(formattedEvents.flat())

      // Check if there are unique categories to update
      if (CalendarData.length > 0) {
        const uniqueCategorySet = new Set([
          ...categoriesOptions, // existing categories
          ...CalendarData.map((data) => data.Category), // new categories
        ])

        const uniqueCategories = Array.from(uniqueCategorySet)

        if (uniqueCategories.length !== categoriesOptions.length) {
          setCategoriesOptions(uniqueCategories)
        }
      }
    } catch (error) {
      console.error('Error fetching events:', error)
    }
  }

  const [categoryOptions, setCategoryOptions] = useState<Array<Dropdown>>()
  useEffect(() => {
    if (categoriesOptions && categoriesOptions.length > 0) {
      const mappedCategoryOptions = categoriesOptions.map((category) => ({
        label: category,
        value: category,
      }))

      if (mappedCategoryOptions.length > 1) {
        mappedCategoryOptions.unshift(ALL_SELECT_OPTION_STRING)
      }

      if (mappedCategoryOptions.length === 1) {
        setInputValues((prev) => ({
          ...prev,
          Category: mappedCategoryOptions[0].value,
        }))
      }
      setCategoryOptions(mappedCategoryOptions)
    } else {
      setCategoryOptions([NONE_SELECT_OPTION_STRING])
    }
  }, [categoriesOptions])

  const calendarEl = useRef<HTMLDivElement | null>(null)

  const redirectToHome = () => {
    navigate(`${ROUTE_TYPE}`)
  }

  useEffect(() => {
    fetchEvents()
  }, [CalendarData])

  const selectEvent = (info: any) => {
    const event = calendarEvents.find((i) => i.id === info.event?.id)
    setSelectedEvent(event || null)
    setShowEventDetails(true)
  }

  useEffect(() => {
    if (calendarEl.current) {
      const calendar = new Calendar(calendarEl.current, {
        dayMaxEvents: true,
        eventClick: selectEvent,
        eventContent: function (info) {
          const title = info.event.title
          const timeText = info.timeText.replace('a', 'AM').replace('p', 'PM')
          return {
            html: `<div title="${title}"><b>${timeText}</b><span> ${title}</span></div>`,
          }
        },
        events: calendarEvents,
        headerToolbar: {
          center: 'title',
          left: 'prev,next today',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        },
        initialView: 'dayGridMonth',
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, rrulePlugin],
      })

      calendar.render()
    }
  }, [calendarEvents])

  const onAssignedComponentChange = (e) => {
    const option: Dropdown[] = majcoms
      .filter(({ ParentComponentCode }) => ParentComponentCode === e.value)
      .map(({ Label, Value }) => ({ label: Label, value: Value }))
    const intialmajcomCodes = intialMajcomDropdown.map(({ value }) => value)
    const options = option.filter(({ value }) => intialmajcomCodes.includes(value as number))
    if (options.length > 1) options.unshift(ALL_SELECT_OPTION)
    setMajcomDropdown(options as optionsTypes[])
    const majcomValue = options.length === 1 ? (options[0].value as number) : ALL_SELECT_VALUE
    setInputValues((prev) => ({
      ...prev,
      AssignedComponentCode: e.value,
      ServicingMAJCOMCode: majcomValue,
    }))
  }

  const onMajcomChange = (e) => {
    setInputValues((prevState) => ({
      ...prevState,
      ServicingMAJCOM: e.label,
      ServicingMAJCOMCode: e.value,
    }))
  }

  const onSectionChange = (e) => {
    setInputValues((prevState) => ({
      ...prevState,
      Section: e.label,
      SectionCode: e.value,
    }))
  }

  const onCategoryChange = (e) => {
    setInputValues((prevState) => ({
      ...prevState,
      Category: e.value,
    }))
  }
  function onClear() {
    setMajcomDropdown(intialMajcomDropdown)
    setInputValues({
      AssignedComponentCode: intialInputValues.componentValue,
      Category: intialInputValues.categoryValue,
      SectionCode: intialInputValues.sectionValue,
      ServicingMAJCOMCode: intialInputValues.majcomValue,
    })
  }

  useEffect(() => {
    scrollToTop()
    setCurrentPage('calendar')
  }, [])

  return (
    <>
      <div id='scroll'></div>
      <section className='Bodysection'>
        {CalendarDataIsLoading && <PageOverlayBusy />}
        <PageHeader
          extras={
            <div className='d-flex flex-wrap align-items-center'>
              <div className='mx-2 mb-1'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-200 min-w-sm-200  sourcesanspro font-13 customselect'
                  formClassName='form-horizontal dash poppins-semibold font-12 title-color'
                  id='AssignedComponent'
                  inputProps={{
                    className: 'font-12 poppins-medium title-color',
                  }}
                  label='Assigned Component'
                  name='Assigned Component'
                  onChange={onAssignedComponentChange}
                  options={intialComponentDropdown}
                  value={inputValues.AssignedComponentCode}
                />
              </div>
              <div className='mx-2 mb-1'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100  sourcesanspro font-13 customselect'
                  formClassName='form-horizontal dash poppins-semibold font-12 title-color'
                  id='ServicingMAJCOM'
                  inputProps={{
                    className: 'font-12 poppins-medium title-color',
                  }}
                  label='Servicing MAJCOM'
                  name='Servicing MAJCOM'
                  onChange={onMajcomChange}
                  options={majcomDropDown}
                  value={inputValues.ServicingMAJCOMCode}
                />
              </div>
              <div className='mx-2 mb-1'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100  sourcesanspro font-13 customselect'
                  formClassName='form-horizontal dash poppins-semibold font-12 title-color'
                  id='Section'
                  inputProps={{
                    className: 'font-12 poppins-medium title-color',
                  }}
                  label='Section'
                  name='Section'
                  onChange={onSectionChange}
                  options={intialSectionDropdown}
                  value={inputValues.SectionCode}
                />
              </div>
              <div className='mx-2 mb-1'>
                <CustomSelect
                  className='form-sm w-100 max-w-sm-100 min-w-sm-100  sourcesanspro font-13 customselect'
                  formClassName='form-horizontal dash  poppins-semibold font-12 title-color'
                  id='Category'
                  inputProps={{
                    className: 'font-12 poppins-medium title-color',
                  }}
                  label='Category'
                  name='Category'
                  onChange={onCategoryChange}
                  options={categoryOptions}
                  value={inputValues.Category}
                />
              </div>

              <div className='ms-2 mb-1 ms-auto'>
                <Buttons
                  className='btn btn-sm links ms-auto ms-sm-0 ms-md-0 p-0 links-underline'
                  label='Clear All'
                  onClick={onClear}
                  type='button'
                />
              </div>
              <div className='mx-2 mb-1 d-none'>
                <Buttons
                  aria-label='Back'
                  className='btn-sm font-14 btn-bgcolor7 whitetext  border-radius text-uppercase'
                  icon='icon-left-arrow me-1 font-10'
                  label='Back'
                  onClick={(e: any) => redirectToHome()}
                />
              </div>
            </div>
          }
          icon='icon-calendar me-1 color-primary'
          name='Calendar'
        />
        <Modal
          className='modalpopup border-radius2'
          isOpen={showEventDetails}
          toggle={() => setShowEventDetails(false)}
          wrapClassName='modalpopupmain'
        >
          <ModalBody>
            <EventDetails event={selectedEvent} onClose={() => setShowEventDetails(false)} />
          </ModalBody>
        </Modal>
        <div className='container'>
          <div className='card shadow mt-3'>
            <div className='card-body'>
              <div className='row'>
                <div className='col-md-12 mt-0'>
                  <div ref={calendarEl}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default CalendarInnerview
